import * as Sentry from "@sentry/react";
import { jwtDecode } from "jwt-decode";
import { createContext, useCallback, useEffect, useState } from "react";

import isPortalPublicPath, { isWhatsNewUnsubscribePath } from "~/auth/publicPaths";
import sfAuth from "~/auth/SFAuth";
import useInterval from "~/auth/useInterval";
import Config from "~/config";
import logger from "~/helpers/logger";
import { CANONICAL_PORTAL_LOGIN_PATH, PORTAL_SESSION_HANDOFF_PATH } from "~portal/helpers/canonicalPortalHost";

const defaultAuthContext: AuthContext = {
	currentUser: null,
	error: null,
	isCustomer: false,
	loggedIn: false,
	performCodeExchange: () => Promise.resolve(),
	redirectToLogin: () => {},
	redirectToLogout: () => {},
	sessionChecked: false,
	wsapiTenant: "",
	wsUserType: ""
};

// biome-ignore lint/style/useComponentExportOnlyModules: AuthContext needs to be exported for consumers
export const AuthContext = createContext<AuthContext>(defaultAuthContext);

interface AuthProviderProps {
	children: React.ReactNode;
}

const TOKEN_DURATION_SECONDS = Config.tokenDuration;

const PUBLIC_UNAUTHENTICATED_PATH_PREFIXES = [
	"/no-redirect",
	"/forbidden",
	"/register",
	"/start-trial",
	"/m365-admin-consent"
];
const PORTAL_HANDOFF_FLOW_PATH_PREFIXES = [CANONICAL_PORTAL_LOGIN_PATH, PORTAL_SESSION_HANDOFF_PATH];
const isPortalHandoffFlowPath = () =>
	PORTAL_HANDOFF_FLOW_PATH_PREFIXES.some((prefix) => window.location.pathname.startsWith(prefix));
const isPublicUnauthenticatedPath = () =>
	PUBLIC_UNAUTHENTICATED_PATH_PREFIXES.some((prefix) => window.location.pathname.startsWith(prefix)) ||
	isPortalHandoffFlowPath() ||
	isPortalPublicPath(window.location.pathname) ||
	isWhatsNewUnsubscribePath(window.location.pathname);
const isPublicRegisterPath = () =>
	window.location.pathname.startsWith("/register") ||
	window.location.pathname.startsWith("/start-trial") ||
	window.location.pathname.startsWith("/m365-admin-consent") ||
	isPortalHandoffFlowPath() ||
	isPortalPublicPath(window.location.pathname) ||
	isWhatsNewUnsubscribePath(window.location.pathname);

/** Derive display name and email from OIDC id_token claims.
 *  Display name fallback: name → given_name + family_name → preferred_username → email
 *  Email fallback: idToken.email → accessToken.email → "" */
function deriveDisplayInfo(idToken: string | undefined, accessTokenEmail: string | undefined) {
	let firstName = "";
	let lastName = "";
	let email = accessTokenEmail || "";
	let displayName = email;

	if (idToken) {
		try {
			const claims = jwtDecode<OIDCIdTokenClaims>(idToken);

			// Prefer id_token email over access token email
			if (claims.email) {
				email = claims.email;
			}
			displayName = email;

			if (claims.name) {
				displayName = claims.name;
				const parts = claims.name.split(" ");
				firstName = parts[0] || "";
				lastName = parts.slice(1).join(" ");
			} else if (claims.given_name || claims.family_name) {
				firstName = claims.given_name || "";
				lastName = claims.family_name || "";
				displayName = `${firstName} ${lastName}`.trim();
			} else if (claims.preferred_username) {
				displayName = claims.preferred_username;
			}
		} catch {
			logger.warn("Failed to decode id_token for display info");
		}
	}

	return { displayName, email, firstName, lastName };
}

function AuthProvider({ children }: AuthProviderProps) {
	const [loggedIn, setLoggedIn] = useState(false);
	const [sessionChecked, setSessionChecked] = useState(false);
	const [currentUser, setCurrentUser] = useState<Maybe<SFUser>>(null);
	const [authError, setAuthError] = useState<Maybe<string>>(null);
	const [wsapiTenant, setWsapiTenant] = useState("");
	const [wsUserType, setWsUserType] = useState("");
	const [isCustomer, setIsCustomer] = useState(false);

	const setUserFromTokens = useCallback(() => {
		const tokens = sfAuth.getTokens();
		if (!tokens) {
			setCurrentUser(null);
			return;
		}

		const decoded = jwtDecode<SFJWT>(tokens.accessToken);
		const userType = decoded.wsUserType ?? "";
		setWsapiTenant(decoded.wsTenantId);
		setWsUserType(userType);
		setIsCustomer(userType === "customer" && !!decoded.wsCustomerId);

		const { displayName, email, firstName, lastName } = deriveDisplayInfo(tokens.idToken, decoded.email);

		setCurrentUser({
			displayName,
			email,
			firstName,
			lastName,
			roles: decoded.roles || [],
			uuid: decoded.sub,
			wsapiTenant: decoded.wsTenantId
		});
	}, []);

	/** Run initial session check */
	useEffect(() => {
		(async () => {
			try {
				const currentToken = await sfAuth.checkSession();
				if (currentToken === "") {
					if (isPublicUnauthenticatedPath()) {
						return;
					}
					sfAuth.redirectToLogin();
				} else {
					setUserFromTokens();
					setLoggedIn(true);
				}
			} catch {
				setLoggedIn(false);
			} finally {
				setSessionChecked(true);
			}
		})();

		const handleOnline = () => sfAuth.checkSession();
		window.addEventListener("online", handleOnline);
		return () => {
			window.removeEventListener("online", handleOnline);
		};
	}, [setUserFromTokens]);

	/** Check session/renew access token periodically */
	useInterval(
		() => {
			(async () => {
				try {
					const currentToken = await sfAuth.checkSession();
					//if not authorized (not just can't connect or offline) then redirect to login
					if (currentToken === "") {
						if (!isPublicRegisterPath()) {
							await sfAuth.redirectToLogin();
						}
					} else {
						try {
							setUserFromTokens();
						} catch (e: unknown) {
							logger.warn("Error parsing tokens:", e);
							Sentry.addBreadcrumb({ category: "auth", level: "error", message: `Error parsing tokens: ${e}` });
						}
					}
				} catch (e) {
					logger.warn("Error checking session:", e);
					Sentry.addBreadcrumb({ category: "auth", level: "error", message: `Error checking session: ${e}` });
					if (!isPublicRegisterPath()) {
						await sfAuth.redirectToLogin();
					}
				}
			})();
		},
		(TOKEN_DURATION_SECONDS * 1000) / 10
	);

	/** Perform OAuth Authorization Code Flow code exchange with the provided `code` */
	async function performCodeExchange(code: string): Promise<void> {
		try {
			await sfAuth.exchangePKCECodeForTokens(code);
			setUserFromTokens();
			setLoggedIn(true);
		} catch (e) {
			const message = e instanceof Error ? e.message : String(e);
			Sentry.addBreadcrumb({ category: "auth", level: "error", message: `Error performing code exchange: ${message}` });
			setAuthError(message);
			throw e;
		}
	}

	const contextValue: AuthContext = {
		currentUser: currentUser,
		error: authError,
		isCustomer,
		loggedIn,
		performCodeExchange,
		// Bind so callers can destructure these from context without losing `this`.
		redirectToLogin: sfAuth.redirectToLogin.bind(sfAuth),
		redirectToLogout: sfAuth.redirectToLogout.bind(sfAuth),
		sessionChecked,
		wsapiTenant,
		wsUserType
	};

	return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export default AuthProvider;
